package org.teddapp.project.ui.objectives

import org.teddapp.project.ui.teddClickable
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddMenu

import org.teddapp.project.ui.TeddTabs

import org.teddapp.project.ui.TeddTab

import org.teddapp.project.ui.TeddType

import org.teddapp.project.ui.TeddSpace

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.width
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.draw.rotate
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens

/**
 * Année courante exposée pour la fonctionnalité Objectifs.
 * Aligné sur le const calendar (CAL_TODAY_ISO) en attendant un vrai Clock.System.
 */
internal val OBJ_CURRENT_YEAR: Long
    get() = OBJ_TODAY_ISO.substring(0, 4).toLongOrNull() ?: 2026L

/** Date du jour au format ISO (YYYY-MM-DD) — délégué au clock central. */
internal val OBJ_TODAY_ISO: String
    get() = org.teddapp.project.db.TODAY_ISO

private val MOIS_FR = listOf(
    "janv.", "févr.", "mars", "avr.", "mai", "juin",
    "juil.", "août", "sept.", "oct.", "nov.", "déc.",
)

/**
 * Formate une date ISO YYYY-MM-DD en français court ("12 mars") ou long ("12 mars 2026").
 * Retourne null si le format ne match pas.
 */
internal fun formatDateFr(iso: String?, long: Boolean = false): String? {
    if (iso.isNullOrBlank()) return null
    val parts = iso.split("-")
    if (parts.size != 3) return iso
    val year = parts[0].toIntOrNull() ?: return iso
    val month = parts[1].toIntOrNull() ?: return iso
    val day = parts[2].toIntOrNull() ?: return iso
    if (month !in 1..12) return iso
    val mon = MOIS_FR[month - 1]
    return if (long) "$day $mon $year" else "$day $mon"
}

/** Une date ISO est "en retard" si elle est strictement antérieure à aujourd'hui (spec §7.8 + §8.7). */
internal fun isOverdue(iso: String?): Boolean {
    if (iso.isNullOrBlank()) return false
    return iso < OBJ_TODAY_ISO
}

/**
 * Onglets de la liste des objectifs (spec §6.1).
 */
internal enum class ObjectiveTab(val key: String, val label: String) {
    Active("active", "Actifs"),
    Done("done", "Terminés"),
    All("all", "Tous"),
}

/**
 * Construit la liste des années à proposer dans le sélecteur (spec §6.1).
 * Règles :
 *  - L'année courante est TOUJOURS présente.
 *  - Toutes les années pour lesquelles au moins un objectif existe.
 *  - Triées par ordre décroissant (la plus récente à gauche).
 */
internal fun availableYears(objectiveYears: Collection<Long?>): List<Long> {
    val set = objectiveYears.filterNotNull().toMutableSet()
    set += OBJ_CURRENT_YEAR
    return set.sortedDescending()
}

/**
 * Bande sélecteur d'année (pastilles horizontales) + onglets Actifs/Terminés/Tous
 * avec compteurs (spec §6.1).
 */
@Composable
internal fun YearAndTabsRow(
    years: List<Long>,
    selectedYear: Long?,
    onSelectYear: (Long?) -> Unit,
    selectedTab: ObjectiveTab,
    onSelectTab: (ObjectiveTab) -> Unit,
    activeCount: Int,
    doneCount: Int,
    allCount: Int,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S8).padding(bottom = TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4),
    ) {
        TeddTabs(
            tabs = listOf(
                TeddTab(ObjectiveTab.Active.name, ObjectiveTab.Active.label, count = activeCount),
                TeddTab(ObjectiveTab.Done.name, ObjectiveTab.Done.label, count = doneCount),
                TeddTab(ObjectiveTab.All.name, ObjectiveTab.All.label, count = allCount),
            ),
            selected = selectedTab.name,
            onSelect = { name -> ObjectiveTab.entries.firstOrNull { it.name == name }?.let(onSelectTab) },
        )
        Row(
            modifier = Modifier.weight(1f).horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2, Alignment.End),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            YearChip(label = "Toutes les années", selected = selectedYear == null, onClick = { onSelectYear(null) })
            years.forEach { y ->
                YearChip(label = y.toString(), selected = selectedYear == y, onClick = { onSelectYear(y) })
            }
        }
    }
}

@Composable
private fun YearChip(label: String, selected: Boolean, onClick: () -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RFull)
    Box(
        modifier = Modifier
            .teddClickable(
                onClick = onClick,
                shape = shape,
                fill = if (selected) TeddTokens.LimeSoft else Color.Transparent,
                hover = if (selected) TeddTokens.LimeSoft else TeddTokens.SurfaceSunken,
            )
            .then(if (selected) Modifier else Modifier.border(1.dp, TeddTokens.Line, shape))
            .height(28.dp)
            .padding(horizontal = TeddSpace.S3),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = label,
            color = if (selected) TeddTokens.Ink else TeddTokens.InkMuted,
            style = TeddType.Caption.copy(fontWeight = FontWeight(520)),
            maxLines = 1,
        )
    }
}

@Composable
private fun TabPill(label: String, count: Int, selected: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .height(24.dp)
            .clip(RoundedCornerShape(TeddTokens.RXs))
            .background(
                if (selected) TeddTokens.Surface else androidx.compose.ui.graphics.Color.Transparent,
                RoundedCornerShape(TeddTokens.RXs),
            )
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(5.dp),
    ) {
        Text(
            text = label,
            color = if (selected) TeddTokens.Ink else TeddTokens.InkMuted,
            fontSize = 11.5.sp,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
        )
        Text(
            text = "$count",
            color = if (selected) TeddTokens.InkMuted else TeddTokens.InkSubtle,
            fontSize = 11.sp,
        )
    }
}

/**
 * Filtre par compétence : un menu compact, « Compétence : Toutes » ou le nom retenu, avec
 * le nombre d'objectifs rattachés à chacune. Une rangée de puces, c'était une pastille par
 * compétence : avec vingt compétences, elle poussait la liste hors de l'écran.
 */
@Composable
internal fun ObjectivesSkillChips(
    skills: List<com.tedd.db.Skill>,
    selectedSkill: String?,
    countFor: (String) -> Int,
    onSelect: (String?) -> Unit,
    modifier: Modifier = Modifier,
    onOpenSkill: ((String) -> Unit)? = null,
) {
    // Une compétence sans objectif ne peut rien filtrer ; on garde celles qui trient
    // quelque chose, plus celle qui est déjà retenue.
    val utiles = skills.filter { it.archived == 0L && (countFor(it.id) > 0 || it.id == selectedSkill) }
    if (utiles.isEmpty()) return
    val current = utiles.firstOrNull { it.id == selectedSkill }
    var open by remember { mutableStateOf(false) }
    Row(modifier = modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Box {
            Row(
                modifier = Modifier
                    .height(32.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, if (current != null) skillColor(current) else TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = { open = true })
                    .padding(horizontal = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                if (current != null) SkillDot(skillColor(current))
                Text(
                    text = if (current == null) "Compétence : toutes" else "${current.name} · ${countFor(current.id)}",
                    color = if (current == null) TeddTokens.InkMuted else TeddTokens.Ink,
                    fontSize = 12.sp,
                    fontWeight = if (current == null) FontWeight.Medium else FontWeight.SemiBold,
                    maxLines = 1,
                )
                Box(modifier = Modifier.size(10.dp).rotate(90f)) {
                    TeddIcon(name = "chevron", size = 10.dp, color = TeddTokens.InkMuted)
                }
            }
            // Vingt intitulés longs : une largeur confortable, la recherche au-delà de huit.
            TeddMenu(
                expanded = open,
                onDismiss = { open = false },
                width = 420.dp,
                placeholder = "Chercher une compétence…",
                entries = listOf(TeddMenuEntry("Toutes", selected = selectedSkill == null, onClick = { onSelect(null) })) +
                    utiles.map { sk ->
                        val color = skillColor(sk)
                        TeddMenuEntry(
                            sk.name,
                            hint = countFor(sk.id).toString(),
                            selected = selectedSkill == sk.id,
                            leading = { SkillDot(color) },
                            onClick = { onSelect(if (selectedSkill == sk.id) null else sk.id) },
                        )
                    },
            )
        }
        // La compétence retenue se suit dans sa fiche : parcours, heures, palier.
        if (selectedSkill != null && onOpenSkill != null) {
            SkillChip(label = "ouvrir la compétence →", selected = false, color = TeddTokens.LimeStrong) { onOpenSkill(selectedSkill) }
        }
    }
}

@Composable
private fun skillColor(sk: com.tedd.db.Skill): Color =
    runCatching { Color(("FF" + sk.color.trimStart('#')).toLong(16)) }.getOrDefault(TeddTokens.LimeStrong)

@Composable
private fun SkillDot(color: Color) {
    Box(modifier = Modifier.size(8.dp).clip(RoundedCornerShape(TeddTokens.RFull)).background(color))
}

@Composable
private fun SkillChip(label: String, selected: Boolean, color: Color, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(TeddTokens.RFull))
            .background(if (selected) color.copy(alpha = 0.14f) else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 12.dp, vertical = 6.dp),
    ) {
        Text(
            text = label,
            color = if (selected) color else TeddTokens.InkMuted,
            fontSize = 12.sp,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
        )
    }
}
